import { useEffect } from "react";
import {
  createFileRoute,
  Link,
  Outlet,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router";
import { Settle } from "@/ui/Notice";
import { Strip } from "@/ui/Strip";
import { useSession } from "@/lib/session";
import { Head } from "@/ui/frame/Page";
import { Button } from "@/ui/Button";
import {
  PLATFORM_ACTION_SLOT,
  PLATFORM_SPINE_SLOT,
  platformScreenFor,
} from "@/features/platform/screens";
import { cx } from "@/lib/format";

export const Route = createFileRoute("/_app/admin")({
  component: AdminLayout,
});

function AdminLayout() {
  const { user } = useSession();
  const navigate = useNavigate();
  const allowed = user?.is_platform_admin === true;

  // The API refuses every request on this surface to anyone else — and refuses
  // it as a 404, so it is invisible rather than forbidden. This is only so a
  // customer who reaches the URL sees their own dashboard instead of a page of
  // failed requests.
  useEffect(() => {
    if (user && !allowed) void navigate({ to: "/", replace: true });
  }, [user, allowed, navigate]);

  if (!allowed) return null;

  // The staff surface needs a second factor, and the API refuses every screen
  // on it without one.
  //
  // Said here, once, rather than left to twelve screens each showing their own
  // refusal. Somebody who has been granted this and meets a wall of errors
  // concludes the grant did not work; somebody who is told what is missing goes
  // and fixes it in a minute.
  if (user?.mfa_enabled === false) {
    return (
      <div className="flex flex-col gap-6">
        <Head
          title="Platform"
          description="Every organisation on this installation."
        />
        <div
          className={cx(
            "flex items-start gap-4 rounded-[var(--radius)] border px-5 py-4",
            "border-[color-mix(in_oklab,_var(--warning)_35%,_transparent)] bg-[color-mix(in_oklab,_var(--warning)_12%,_transparent)]",
          )}
        >
          <span
            className="mt-0.5 shrink-0 text-[var(--warning)]"
            aria-hidden="true"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <path
                d="M12 2.6l7.5 2.7v6.2c0 4.5-3 8-7.5 9.9-4.5-1.9-7.5-5.4-7.5-9.9V5.3L12 2.6z"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinejoin="round"
              />
              <path d="M12 8.6v4" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
              <circle cx="12" cy="15.6" r="1.1" fill="currentColor" />
            </svg>
          </span>
          <div className="min-w-0">
            <p className="t-sub text-[var(--warning)]">
              Turn on a second factor to use the platform surface
            </p>
            <p className="t-body mt-1 max-w-prose">
              This surface can read every tenant&apos;s account, stop every service and erase a
              customer. A password alone is one phishing email away from all of that, and yours is
              the one account on this installation nobody else can recover.
            </p>
            <Link to="/account/security" className="mt-3 inline-block">
              <Button tone="primary" size="dense">
                Set one up
              </Button>
            </Link>
            <p className="t-meta mt-2">
              Sign out and back in afterwards, so the session carries it.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return <PlatformSurface />;
}

/**
 * The staff surface, which is now a title and a screen.
 *
 * # WHAT IT WAS
 *
 * Five vertical levels before the first row of data: a page title with a
 * sentence under it, a segmented control of five groups, an underlined tab row
 * of that group's screens with an aside about recording on the right, and then
 * whatever the screen itself drew — which for thirteen of them was a search
 * strip and a filter row. On a laptop the first table row started below the
 * fold of a 900-pixel window.
 *
 * # WHAT FIXED IT
 *
 * Not tightening the spacing. The levels were duplicates: the sidebar already
 * listed eight of these screens while the chrome listed all twenty-one again.
 * Twenty-one destinations went to the sidebar, where the vertical axis has
 * room for them, and the two rows of furniture went with them.
 *
 * What is left is what a screen needs to say: which one it is, what it holds,
 * and what you can do to it from here. One level.
 *
 * # WHY THE RECORDING NOTICE IS NOT HERE ANY MORE
 *
 * It was a line of small print on the right of the tab row, which is both too
 * easy to miss and, as furniture, too expensive. It is a property of being on
 * this surface at all rather than of any screen, so it is said by the context
 * badge in the header — permanently, in the corner of the eye, costing no
 * vertical space — and spelled out in full on the overview.
 */
function PlatformSurface() {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const here = platformScreenFor(pathname);

  return (
    <>
      {/* Only for a screen in the navigation. A detail screen — one
          organisation, one person — carries its own title, which is the name
          of the thing being looked at; printing the surface's name above that
          would be the duplication this change removed, the other way round. */}
      {here && (
        <Head
          title={here.label}
          description={here.blurb}
          // The slot a screen portals its primary action into. Always
          // rendered, so it exists before the screen below looks for it;
          // empty, it takes no space.
          actions={<span id={PLATFORM_ACTION_SLOT} className="flex items-center gap-2" />}
          // No tab row here any more: the sidebar lists every screen of
          // every group at once, so repeating the open group's screens under
          // the title would be the same navigation twice.
        />
      )}

      {/* What the screen has to report, across the top rather than down the
          side. The bar carries the five groups, the tab row carries the
          screens of the open one, and this carries the figures — three levels
          in 44, 36 and 64 pixels of height, against a 320px column that
          carried the second and third together and took a fifth of every
          table's width to do it. */}
      <Strip slotId={PLATFORM_SPINE_SLOT} />

      <Settle>
        <Outlet />
      </Settle>
    </>
  );
}
